import React, { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Building2,
  CheckCircle2,
  Download,
  FileText,
  ImagePlus,
  Palette,
  RefreshCw,
  Save,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import { useAuth } from '@/core/hooks';
import {
  type CompanyDocumentAutomation,
  type CompanyProfile,
  defaultCompanyProfile,
  formatCompanyAddress,
  loadCompanyProfile,
  saveCompanyProfile,
} from '@/app/utils/companyProfile';

const documentAutomationLabels: Array<{ key: keyof CompanyDocumentAutomation; label: string; description: string }> = [
  { key: 'termsAndConditions', label: 'Terms and Conditions', description: 'Uses company name, trading name, contact details and subscription/legal owner details.' },
  { key: 'privacyPolicy', label: 'Privacy Policy', description: 'Autofills controller details, data protection contact, postal address and email.' },
  { key: 'dataProcessingAgreement', label: 'Data Processing Agreement', description: 'Autofills controller/processor names, registered office and data protection lead.' },
  { key: 'cookiePolicy', label: 'Cookie Policy', description: 'Uses company website, contact email and service description.' },
  { key: 'dpia', label: 'DPIA', description: 'Creates a UK care software DPIA draft with company, branch and data lead details.' },
  { key: 'ropa', label: 'ROPA Register', description: 'Creates Records of Processing Activities entries for clients, carers, family users and payroll.' },
  { key: 'backupRestorePolicy', label: 'Backup and Restore Policy', description: 'Adds company owner, escalation route and continuity contacts.' },
  { key: 'incidentResponsePolicy', label: 'Incident Response Policy', description: 'Adds named contacts for data incidents, safeguarding and operational escalation.' },
  { key: 'processorContracts', label: 'Processor Contracts Register', description: 'Tracks suppliers, API providers, hosting, email, SMS, storage and AI processors.' },
  { key: 'cqcEvidencePack', label: 'CQC Evidence Pack', description: 'Uses CQC provider details, registered manager and governance contacts.' },
  { key: 'employmentContract', label: 'Employment Contract Templates', description: 'Autofills employer details, branch address, contact details and signature blocks.' },
  { key: 'clientContract', label: 'Client Contract Templates', description: 'Autofills care provider details, service contacts and emergency contact wording.' },
  { key: 'payrollDocuments', label: 'Payroll Documents', description: 'Autofills company logo, payroll contact, address and legal entity details.' },
];

const legalRequirementRows = [
  'Company identity: legal entity, trading name, registered address and contact details.',
  'CQC readiness: provider ID, registered manager, nominated individual and evidence owner.',
  'UK GDPR: ICO registration, privacy contact, data protection lead, DPIA and ROPA records.',
  'Employment documents: employer details, contract templates, payroll contact and signature fields.',
  'Client documents: service agreement, privacy wording, emergency contact and complaints route.',
  'Operational policies: backup, restore, incident response, processor register and audit pack.',
];

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">{hint}</span>}
    </label>
  );
}

function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 ${props.className ?? ''}`}
    />
  );
}

function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`min-h-24 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 ${props.className ?? ''}`}
    />
  );
}

function downloadText(filename: string, content: string, type = 'application/json') {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function createLegalPackPreview(profile: CompanyProfile) {
  const address = formatCompanyAddress(profile);
  return [
    `${profile.publicName} - UK Legal Personalisation Pack`,
    '',
    `Legal entity: ${profile.legal.legalEntityName || profile.publicName}`,
    `Trading name: ${profile.legal.tradingName || profile.publicName}`,
    `Company number: ${profile.legal.companyNumber || 'To be completed'}`,
    `CQC provider ID: ${profile.legal.cqcProviderId || 'To be completed'}`,
    `ICO registration: ${profile.legal.icoRegistration || 'To be completed'}`,
    `Registered manager: ${profile.legal.registeredManager || 'To be completed'}`,
    `Nominated individual: ${profile.legal.nominatedIndividual || 'To be completed'}`,
    `Data protection lead: ${profile.legal.dataProtectionLead || 'To be completed'}`,
    `Address: ${address}`,
    `Phone: ${profile.phone}`,
    `On-call: ${profile.onCallPhone}`,
    `Email: ${profile.email}`,
    `Website: ${profile.website}`,
    '',
    'Enabled document automations:',
    ...documentAutomationLabels
      .filter((item) => profile.documentAutomation[item.key])
      .map((item) => `- ${item.label}: ${item.description}`),
    '',
    'Human review note: generated legal and compliance documents remain drafts until reviewed and approved by the responsible UK legal/compliance professional.',
  ].join('\n');
}

export const BrandingSettings: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = Boolean(user?.roles.includes('SUPER_ADMIN'));
  const [profile, setProfile] = useState<CompanyProfile>(() => loadCompanyProfile());
  const [activeTab, setActiveTab] = useState<'profile' | 'brand' | 'legal' | 'documents' | 'import'>('profile');
  const importInputRef = useRef<HTMLInputElement>(null);
  const fullLogoInputRef = useRef<HTMLInputElement>(null);
  const markLogoInputRef = useRef<HTMLInputElement>(null);

  const address = useMemo(() => formatCompanyAddress(profile), [profile]);
  const legalPackPreview = useMemo(() => createLegalPackPreview(profile), [profile]);

  const update = <K extends keyof CompanyProfile>(field: K, value: CompanyProfile[K]) => {
    setProfile((current) => ({ ...current, [field]: value }));
  };

  const save = () => {
    saveCompanyProfile(profile);
    toast.success('Company profile saved and applied to the application.');
  };

  const reset = () => {
    setProfile(defaultCompanyProfile);
    saveCompanyProfile(defaultCompanyProfile);
    toast.success('Default Fuchsia company profile restored.');
  };

  const exportConfig = () => {
    downloadText(
      `${profile.publicName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-company-profile.json`,
      JSON.stringify({ version: 1, profile }, null, 2)
    );
  };

  const exportLegalPreview = () => {
    downloadText(
      `${profile.publicName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-uk-legal-pack-preview.txt`,
      legalPackPreview,
      'text/plain'
    );
  };

  const importConfig = async (file: File) => {
    try {
      const content = await file.text();
      const parsed = JSON.parse(content) as { profile?: Partial<CompanyProfile> } | Partial<CompanyProfile>;
      const imported = 'profile' in parsed ? parsed.profile : parsed;
      if (!imported?.publicName && !imported?.brandName) throw new Error('Missing company name');
      const nextProfile: CompanyProfile = {
        ...defaultCompanyProfile,
        ...imported,
        openingHours: { ...defaultCompanyProfile.openingHours, ...imported.openingHours },
        legal: { ...defaultCompanyProfile.legal, ...imported.legal },
        documentAutomation: { ...defaultCompanyProfile.documentAutomation, ...imported.documentAutomation },
        updatedAt: new Date().toISOString(),
      };
      setProfile(nextProfile);
      saveCompanyProfile(nextProfile);
      toast.success('New company data imported and applied.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not import company profile.');
    }
  };

  const uploadLogo = async (file: File, target: 'logoFullUrl' | 'logoMarkUrl') => {
    const dataUrl = await readFileAsDataUrl(file);
    update(target, dataUrl);
    toast.success(target === 'logoFullUrl' ? 'Full logo loaded.' : 'Logo mark loaded.');
  };

  if (!isSuperAdmin) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-950">
        <h1 className="text-xl font-bold">Company setup unavailable</h1>
        <p className="mt-2 text-sm">Only the application owner can edit global white-label and legal personalisation settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-lg border border-purple-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="grid gap-6 p-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-[#6c3a80] dark:border-purple-900 dark:bg-purple-950/40 dark:text-fuchsia-200">
              <Building2 className="h-4 w-4" />
              Owner company setup
            </div>
            <h1 className="mt-4 text-3xl font-bold tracking-normal text-slate-950 dark:text-white">Company White-label & UK Legal Personalisation</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Edit the company identity once, then apply it across branding, contact panels, payroll PDFs, legal document drafts, compliance packs and future subscription deployments for other care providers.
            </p>
            <div className="mt-5 grid gap-3 text-sm text-slate-700 dark:text-slate-300 sm:grid-cols-3">
              <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-900">
                <p className="font-bold text-slate-950 dark:text-white">{profile.publicName}</p>
                <p className="mt-1 text-xs">{address}</p>
              </div>
              <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-900">
                <p className="font-bold text-slate-950 dark:text-white">Contact</p>
                <p className="mt-1 text-xs">{profile.phone} | On-call {profile.onCallPhone}</p>
                <p className="text-xs">{profile.email}</p>
              </div>
              <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-900">
                <p className="font-bold text-slate-950 dark:text-white">Automation</p>
                <p className="mt-1 text-xs">{documentAutomationLabels.filter((item) => profile.documentAutomation[item.key]).length} document modules enabled</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-purple-50 to-white p-5 dark:border-slate-800 dark:from-slate-900 dark:to-slate-950">
            <div className="flex items-center gap-4">
              <div className="grid h-20 w-20 shrink-0 place-items-center rounded-md bg-white shadow-sm ring-1 ring-purple-100 dark:bg-slate-950 dark:ring-slate-800">
                <img src={profile.logoMarkUrl || defaultCompanyProfile.logoMarkUrl} alt="" className="h-16 w-16 object-contain" />
              </div>
              <div className="min-w-0">
                <img src={profile.logoFullUrl || defaultCompanyProfile.logoFullUrl} alt={profile.publicName} className="max-h-16 max-w-full object-contain" />
                <p className="mt-3 text-sm font-semibold text-slate-700 dark:text-slate-200">{profile.tagline}</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-2">
              {[profile.primaryColor, profile.secondaryColor, profile.accentColor].map((color) => (
                <div key={color} className="h-10 rounded-md border border-white shadow-sm" style={{ background: color }} title={color} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap gap-2">
        {[
          ['profile', 'Company data'],
          ['brand', 'Logo & colours'],
          ['legal', 'UK legal details'],
          ['documents', 'Document automation'],
          ['import', 'Import / export'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id as typeof activeTab)}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition ${
              activeTab === id
                ? 'bg-[#6c3a80] text-white shadow-sm'
                : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'profile' && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <h2 className="text-xl font-bold text-slate-950 dark:text-white">Company identity and contact details</h2>
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <Field label="Application / brand name"><TextInput value={profile.brandName} onChange={(event) => update('brandName', event.target.value)} /></Field>
            <Field label="Public care provider name"><TextInput value={profile.publicName} onChange={(event) => update('publicName', event.target.value)} /></Field>
            <Field label="Tagline"><TextInput value={profile.tagline} onChange={(event) => update('tagline', event.target.value)} /></Field>
            <Field label="Website"><TextInput value={profile.website} onChange={(event) => update('website', event.target.value)} /></Field>
            <Field label="Address line 1"><TextInput value={profile.addressLine1} onChange={(event) => update('addressLine1', event.target.value)} /></Field>
            <Field label="Address line 2"><TextInput value={profile.addressLine2} onChange={(event) => update('addressLine2', event.target.value)} /></Field>
            <Field label="Town / city"><TextInput value={profile.townCity} onChange={(event) => update('townCity', event.target.value)} /></Field>
            <Field label="County"><TextInput value={profile.county} onChange={(event) => update('county', event.target.value)} /></Field>
            <Field label="Postcode"><TextInput value={profile.postcode} onChange={(event) => update('postcode', event.target.value)} /></Field>
            <Field label="Country"><TextInput value={profile.country} onChange={(event) => update('country', event.target.value)} /></Field>
            <Field label="Main telephone"><TextInput value={profile.phone} onChange={(event) => update('phone', event.target.value)} /></Field>
            <Field label="On-call telephone"><TextInput value={profile.onCallPhone} onChange={(event) => update('onCallPhone', event.target.value)} /></Field>
            <Field label="Company email"><TextInput type="email" value={profile.email} onChange={(event) => update('email', event.target.value)} /></Field>
          </div>
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <Field label="Office opening hours"><TextArea value={profile.openingHours.weekdayOffice} onChange={(event) => update('openingHours', { ...profile.openingHours, weekdayOffice: event.target.value })} /></Field>
            <Field label="Weekday emergency hours"><TextArea value={profile.openingHours.weekdayEmergency} onChange={(event) => update('openingHours', { ...profile.openingHours, weekdayEmergency: event.target.value })} /></Field>
            <Field label="Weekend emergency hours"><TextArea value={profile.openingHours.weekendEmergency} onChange={(event) => update('openingHours', { ...profile.openingHours, weekendEmergency: event.target.value })} /></Field>
          </div>
        </section>
      )}

      {activeTab === 'brand' && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <h2 className="text-xl font-bold text-slate-950 dark:text-white">Logo, colours and visual identity</h2>
          <div className="mt-5 grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <Field label="Full logo URL or uploaded image" hint="Use a wide logo for login, header, PDFs and generated documents.">
                <div className="flex gap-2">
                  <TextInput value={profile.logoFullUrl} onChange={(event) => update('logoFullUrl', event.target.value)} />
                  <button type="button" onClick={() => fullLogoInputRef.current?.click()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 text-sm font-semibold">
                    <Upload className="h-4 w-4" /> Upload
                  </button>
                </div>
              </Field>
              <Field label="Logo mark URL or uploaded image" hint="Use a square mark for sidebar, app icon, document watermark and compact headers.">
                <div className="flex gap-2">
                  <TextInput value={profile.logoMarkUrl} onChange={(event) => update('logoMarkUrl', event.target.value)} />
                  <button type="button" onClick={() => markLogoInputRef.current?.click()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 text-sm font-semibold">
                    <ImagePlus className="h-4 w-4" /> Upload
                  </button>
                </div>
              </Field>
              <input ref={fullLogoInputRef} type="file" accept="image/*" hidden onChange={(event) => event.target.files?.[0] && void uploadLogo(event.target.files[0], 'logoFullUrl')} />
              <input ref={markLogoInputRef} type="file" accept="image/*" hidden onChange={(event) => event.target.files?.[0] && void uploadLogo(event.target.files[0], 'logoMarkUrl')} />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                ['Primary colour', 'primaryColor'],
                ['Secondary colour', 'secondaryColor'],
                ['Accent colour', 'accentColor'],
              ].map(([label, key]) => (
                <Field key={key} label={label}>
                  <input type="color" value={profile[key as keyof CompanyProfile] as string} onChange={(event) => update(key as keyof CompanyProfile, event.target.value as never)} className="h-11 w-full rounded-md border border-slate-300 bg-white" />
                  <TextInput value={profile[key as keyof CompanyProfile] as string} onChange={(event) => update(key as keyof CompanyProfile, event.target.value as never)} className="mt-2" />
                </Field>
              ))}
            </div>
          </div>
        </section>
      )}

      {activeTab === 'legal' && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <h2 className="text-xl font-bold text-slate-950 dark:text-white">UK legal and compliance personalisation fields</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            These fields are used to personalise legal drafts and compliance packs. Documents should still be reviewed by the responsible UK legal or compliance professional before use.
          </p>
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <Field label="Legal entity name"><TextInput value={profile.legal.legalEntityName} onChange={(event) => update('legal', { ...profile.legal, legalEntityName: event.target.value })} /></Field>
            <Field label="Trading name"><TextInput value={profile.legal.tradingName} onChange={(event) => update('legal', { ...profile.legal, tradingName: event.target.value })} /></Field>
            <Field label="Companies House number"><TextInput value={profile.legal.companyNumber} onChange={(event) => update('legal', { ...profile.legal, companyNumber: event.target.value })} /></Field>
            <Field label="VAT number"><TextInput value={profile.legal.vatNumber} onChange={(event) => update('legal', { ...profile.legal, vatNumber: event.target.value })} /></Field>
            <Field label="CQC provider ID"><TextInput value={profile.legal.cqcProviderId} onChange={(event) => update('legal', { ...profile.legal, cqcProviderId: event.target.value })} /></Field>
            <Field label="ICO registration"><TextInput value={profile.legal.icoRegistration} onChange={(event) => update('legal', { ...profile.legal, icoRegistration: event.target.value })} /></Field>
            <Field label="Registered manager"><TextInput value={profile.legal.registeredManager} onChange={(event) => update('legal', { ...profile.legal, registeredManager: event.target.value })} /></Field>
            <Field label="Nominated individual"><TextInput value={profile.legal.nominatedIndividual} onChange={(event) => update('legal', { ...profile.legal, nominatedIndividual: event.target.value })} /></Field>
            <Field label="Data protection lead"><TextInput value={profile.legal.dataProtectionLead} onChange={(event) => update('legal', { ...profile.legal, dataProtectionLead: event.target.value })} /></Field>
          </div>
          <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
            <h3 className="flex items-center gap-2 text-sm font-bold text-slate-950 dark:text-white"><ShieldCheck className="h-4 w-4 text-[#6c3a80]" /> UK coverage checklist</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {legalRequirementRows.map((row) => (
                <p key={row} className="flex gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  {row}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {activeTab === 'documents' && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-950 dark:text-white">Automatic document personalisation</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                Choose which legal, operational and commercial documents should be filled automatically with the current company profile.
              </p>
            </div>
            <button type="button" onClick={exportLegalPreview} className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200">
              <FileText className="h-4 w-4" /> Download legal pack preview
            </button>
          </div>
          <div className="mt-5 grid gap-3 lg:grid-cols-2">
            {documentAutomationLabels.map((item) => (
              <label key={item.key} className="flex cursor-pointer gap-3 rounded-md border border-slate-200 bg-slate-50 p-4 hover:border-purple-200 dark:border-slate-800 dark:bg-slate-900">
                <input
                  type="checkbox"
                  checked={profile.documentAutomation[item.key]}
                  onChange={(event) => update('documentAutomation', { ...profile.documentAutomation, [item.key]: event.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-[#6c3a80] focus:ring-[#6c3a80]"
                />
                <span>
                  <span className="block text-sm font-bold text-slate-950 dark:text-white">{item.label}</span>
                  <span className="mt-1 block text-xs leading-5 text-slate-600 dark:text-slate-400">{item.description}</span>
                </span>
              </label>
            ))}
          </div>
          <Field label="Current legal pack preview">
            <TextArea value={legalPackPreview} readOnly className="min-h-72 font-mono text-xs" />
          </Field>
        </section>
      )}

      {activeTab === 'import' && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <h2 className="text-xl font-bold text-slate-950 dark:text-white">Fast setup for another care company</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Export this configuration, duplicate it for another provider, edit the JSON values, then import it to rebrand and personalise the application quickly for subscription onboarding.
          </p>
          <div className="mt-5 grid gap-4 lg:grid-cols-3">
            <button type="button" onClick={exportConfig} className="inline-flex min-h-24 flex-col items-start justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left hover:border-purple-200 dark:border-slate-800 dark:bg-slate-900">
              <Download className="h-5 w-5 text-[#6c3a80]" />
              <span className="font-bold text-slate-950 dark:text-white">Export company profile JSON</span>
              <span className="text-xs leading-5 text-slate-600 dark:text-slate-400">Use as a reusable onboarding template for another company.</span>
            </button>
            <button type="button" onClick={() => importInputRef.current?.click()} className="inline-flex min-h-24 flex-col items-start justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left hover:border-purple-200 dark:border-slate-800 dark:bg-slate-900">
              <Upload className="h-5 w-5 text-[#6c3a80]" />
              <span className="font-bold text-slate-950 dark:text-white">Import new company data</span>
              <span className="text-xs leading-5 text-slate-600 dark:text-slate-400">Automatically applies branding, contacts, legal fields and document automation.</span>
            </button>
            <button type="button" onClick={reset} className="inline-flex min-h-24 flex-col items-start justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left hover:border-purple-200 dark:border-slate-800 dark:bg-slate-900">
              <RefreshCw className="h-5 w-5 text-[#6c3a80]" />
              <span className="font-bold text-slate-950 dark:text-white">Restore Fuchsia default</span>
              <span className="text-xs leading-5 text-slate-600 dark:text-slate-400">Returns all branding and company fields to the current Fuchsia setup.</span>
            </button>
          </div>
          <input ref={importInputRef} type="file" accept="application/json,.json" hidden onChange={(event) => event.target.files?.[0] && void importConfig(event.target.files[0])} />
        </section>
      )}

      <div className="sticky bottom-4 z-10 rounded-lg border border-slate-200 bg-white/90 p-4 shadow-lg shadow-slate-950/10 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
            <Palette className="h-5 w-5 text-[#6c3a80]" />
            <span>Changes apply immediately to the browser preview after saving. Server-side multi-tenant storage can reuse the exported JSON structure.</span>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setProfile(loadCompanyProfile())} className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200">
              <RefreshCw className="h-4 w-4" /> Reload
            </button>
            <button type="button" onClick={save} className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#5e3474]">
              <Save className="h-4 w-4" /> Save and apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingSettings;
